<script setup>
    // Libraries
    import { storeToRefs } from 'pinia';
    import { useRouteQuery } from '@vueuse/router'

    // Stores
    import { useUserStore } from '@/stores/user';
    import { useSitesStore } from '@/stores/sites';

    // Variables
    const { sites: userSites } = storeToRefs(useUserStore());
    const mapView = useRouteQuery('mapView', false);
    // Sites Store
    const loading = ref(false);
    const sitesStore = useSitesStore();
    const sitesStoreMap = sitesStore.sitesMap;

    // Computed
    const sites = computed(() => {
        if (userSites.value.length) {
            return userSites.value
                .map((siteId) => sitesStoreMap[siteId])
                .filter((site) => site && site.id);
        }
        return [];
    });

    // Contain failures from the map subtree so they cannot take the page with
    // them. vue3-google-map's clusterer throws from inside a beforeUnmount hook
    // when its index is missing — "Cannot read properties of undefined (reading
    // 'range')" out of clearMarkers -> render -> calculate -> cluster ->
    // getClusters. A throw in a lifecycle hook corrupts Vue's teardown, which
    // kills the whole app rather than just the map: the page goes blank and
    // nothing above the footer stays clickable (Asana O2-68, O2-37).
    //
    // Returning false stops propagation so the error dies here. It is
    // deliberately NOT silent — it still logs, because a map that cannot render
    // is a real problem, just not one worth destroying the page over. Any
    // Maps-side failure (bad referrer, quota, blocked script, offline) lands
    // here too, and all of them used to be fatal.
    onErrorCaptured((error) => {
        console.error('[my-ohc] contained an error from the favourites panel:', error);
        return false;
    });

    // Methods
    function setView(view) {
        mapView.value = view;
    }

</script>

<template>
    <div class="my-ohc-page">
        <BlocksMyOhcNav @viewToggle="setView" />
        <!--
            CLIENT ONLY, deliberately. Favourites are per-user and are fetched by
            a separate client-side call to /api/user/data, so the server has no
            way to know them: SSR always rendered the "no sites favourited" empty
            state, and the client then had to tear that subtree out and replace it
            with the list once the data arrived.

            That swap is what broke the page. Unmounting the map subtree runs
            vue3-google-map's clusterer teardown (clearMarkers -> render ->
            calculate -> cluster -> getClusters), which throws
            "Cannot read properties of undefined (reading 'range')" from inside a
            beforeUnmount hook. A throw there corrupts Vue's teardown mid-flight,
            so the whole app dies — in production it surfaces as "Cannot
            destructure property 'bum' of 'e' as it is null" (`bum` IS the
            beforeUnmount hook list) and Nuxt swaps in its "500 - Internal Server
            Error" page.

            Symptoms this caused: a blank My OHC panel with neither cards nor the
            empty-state message (Asana O2-68), and a page where nothing above the
            footer responds to clicks, because the app is dead and only plain
            anchors still work (O2-37).

            It only ever bit on a FRESH load of this route. Arriving by
            client-side navigation — e.g. from /my-ohc/itineraries — mounts the
            panel once with favourites already in hand, so there is no swap and no
            crash. That is why it looked device- and account-specific for weeks.

            Rendering client-side removes the mismatch at its source: the server
            emits nothing here, so there is no server markup for the client to
            contradict.
        -->
        <ClientOnly>
        <section class="favorites-and-intineraries-wrapper">
            <!-- Gated on the RESOLVED sites, not the raw id list. A favourite
                 whose site is no longer in the synced collection resolves to
                 nothing, so the list rendered empty while this alert stayed
                 hidden — the whole panel came out blank, with no explanation
                 (Asana O2-37). Matches how site-tours.vue already does it. -->
            <BlocksTurquoiseAlert v-if="!loading && !sites.length" :hasSites="sites.length" type="sites" :overlay="mapView" />
            <div class="my-favorites-wrapper">
                <section class="sites">
                    <BlocksMap
                        v-if="mapView"
                        :locations="sites"
                        :mapOnly="!userSites.length"
                        navId="my-ohc-nav"
                        :offset="36"
                    />
                    <ul v-else class="stack container hd-container">
                        <li v-for="(site, index) in sites" :key="index">
                            <CardsSite type="list" :item="site" v-if="site" />
                        </li>
                    </ul>
                </section>
            </div>
        </section>
        </ClientOnly>
    </div>
</template>

<style lang="scss" scoped>
    .my-ohc-page {
        max-width: $hd;
        margin: auto;
        min-height: 400px;
        padding-top: 16px;
        
        .is-iframe & {
            padding-top: 16px;
            margin-bottom: 32px;
        }


        .favorites-and-intineraries-wrapper {
            z-index: 0;
            position: relative;
        }

        .sites ul {
            display: flex;
            flex-direction: column;
            gap: 16px;

            li { 
                .card-site.list  {
                    padding: 0;
                    padding-bottom: 16px;
                }
            }
        }

        @media (min-width: $tablet) {
            margin: auto;

            .sites ul {
                display: grid;
                grid-template-columns: repeat(4, 1fr);
                gap: 15px;
                padding-bottom: 30px;

                li .card-site.list  {
                    padding: 0;
                }
            }
        }
    }
</style>
